import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import _ from 'lodash';
import qs from 'qs';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import { createAuthErrorToast, createErrorToast } from '@arc/frontend/ui-toasts';
import { useDebounceCallback } from '@arc/frontend/util-common';
import { apiRequest } from '@arc/frontend/util-query';
import { ChangePassword, CheckResetPasswordToken, GetSession, Login, Logout, ResetPassword } from '@arc/shared/util-auth';
import { ms } from '@arc/shared/util-js';

/**
 * Debounce is used to prevent toast dismiss/create animations from overlapping
 */
const TOAST_DEBOUNCE = 200;
const keys = {
  all: ['auth'],
  session: () => [...keys.all, 'session'],
  member: avbId => [...keys.all, 'member', avbId],
  allResetPassword: () => [...keys.all, 'resetPassword'],
  checkResetPasswordToken: token => [...keys.allResetPassword(), 'check', token]
};
export const authKeys = keys;
function useRedirectFromLogin() {
  const location = useLocation();
  const navigate = useNavigate();
  return useCallback(() => {
    const {
      r
    } = qs.parse(location.search, {
      ignoreQueryPrefix: true
    });
    const redirect = typeof r === 'string' ? r : null;
    if (redirect) {
      void navigate(redirect);
    } else {
      void navigate('/');
    }
  }, [location, navigate]);
}
export function useMutationLogin() {
  const {
    t
  } = useTranslation('login');
  const queryClient = useQueryClient();
  const redirectFromLogin = useRedirectFromLogin();
  const [debounceToast, cancelDebounceToast] = useDebounceCallback();
  const createInvalidLoginToast = remainingAttempts => debounceToast(() => createAuthErrorToast(t('login fail'), <>
						{t('invalid credentials')}
						<br />
						{t('remaining attempts', {
      count: remainingAttempts
    })}
					</>), TOAST_DEBOUNCE);
  const createLockoutToast = lockoutTimeMins => debounceToast(() => createAuthErrorToast(t('account locked'), t('account locked for', {
    count: lockoutTimeMins
  })), TOAST_DEBOUNCE);
  return useMutation({
    mutationFn: body => apiRequest(Login, {
      body
    }),
    onSuccess(user) {
      queryClient.setQueryData(keys.session(), user);
      redirectFromLogin();
    },
    onError(error) {
      cancelDebounceToast();
      toast.dismiss();
      if (isAxiosError(error) && error.response) {
        const headers = error.response.headers;
        const remainingHeader = headers['x-ratelimit-remaining'];
        const remainingAttempts = Number.parseInt(remainingHeader ?? '', 10);
        const unlockTimeSec = Number.parseInt(headers['x-ratelimit-reset'] ?? headers['retry-after'] ?? '', 10);
        const unlockTimeMin = Math.ceil(unlockTimeSec / 60);
        switch (error.response.status) {
          case 401:
            {
              if (remainingAttempts === 0) {
                createLockoutToast(unlockTimeMin);
              } else {
                createInvalidLoginToast(remainingAttempts);
              }
              return;
            }
          case 429:
            {
              createLockoutToast(unlockTimeMin);
              return;
            }
        }
      }
      createErrorToast(t('unknown error'));
    }
  });
}
export function useCheckResetPasswordTokenQuery(token) {
  return useSuspenseQuery({
    queryKey: keys.checkResetPasswordToken(token),
    queryFn: ({
      signal
    }) => apiRequest(CheckResetPasswordToken, {
      body: {
        token
      }
    }, {
      signal
    }),
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    staleTime: Number.POSITIVE_INFINITY
  });
}
export function useMutationResetPassword() {
  return useMutation({
    mutationFn: body => apiRequest(ResetPassword, {
      body
    })
  });
}
export function useMutationLogout() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: async ({
      noReturn = false
    } = {}) => {
      if (noReturn) {
        sessionStorage.removeItem('redirect');
      }
      await apiRequest(Logout, {});
    },
    onSuccess: () => {
      /**
       * Note: Normally we don't like navigating from a mutation, but in
       * this case it makes sense to navigate to the login page after
       * logout regardless of where in the app the logout occurs
       */
      void navigate('/login');
      queryClient.setQueryData(keys.session(), null);
    },
    onError: () => {
      window.location.reload();
    }
  });
}
export const SESSION_RETRY_COUNT = 5;
export function useSessionQuery() {
  return useSuspenseQuery({
    queryKey: keys.session(),
    // eslint-disable-next-line @tanstack/query/no-void-query-fn
    queryFn: ({
      signal
    }) => apiRequest(GetSession, {}, {
      signal
    }),
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    staleTime: Number.POSITIVE_INFINITY,
    retry: count => count <= SESSION_RETRY_COUNT,
    retryDelay: attempt =>
    // retry after 1, 2, 4, 8, 16 seconds
    Math.min(attempt > 1 ? 2 ** attempt * ms.seconds : 1 * ms.seconds, 30 * ms.seconds)
  });
}
export function useRefreshUser() {
  return useSessionQuery().refetch;
}
export function useResetNonSessionQueries() {
  const queryClient = useQueryClient();
  return useCallback(() => {
    void queryClient.resetQueries({
      predicate: query => !_.isEqual(query.queryKey, authKeys.session())
    });
  }, [queryClient]);
}
export const changePassword = body => apiRequest(ChangePassword, {
  body
});